import { Component, OnInit, ChangeDetectorRef, NgZone, forwardRef, Input, Output, EventEmitter } from '@angular/core';
import { LazyService } from '../../services/lazy.service';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { NgxImageCompressService } from 'ngx-image-compress';
import { BaseService } from '../../shared/services/base.service';
import { AlertsService } from '../../services/alerts.service';
import { StorageService } from '../../shared/services/storage.service';
import { LoaderService } from '../../services/loader.service';
import { DialogService } from '../dialog/dialog.service';
import { DialogModalSize } from '../dialog/dialog-ref';
import { FileManagerComponent } from '../file-manager/file-manager.component';
import { CommunicationService } from '../../services/communication.service';
import { Subscription } from 'rxjs';

declare var tui: any;

@Component({
    selector: 'app-image-editor',
    templateUrl: './image-editor.component.html',
    styleUrls: ['./image-editor.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ImageEditorComponent),
            multi: true
        }
    ],
    standalone: false
})
export class ImageEditorComponent implements ControlValueAccessor, OnInit {
  editor: any;
  selectedImages: any = [];
  originalImageSizes: any;
  firstBatchPromptDone = false;
  draggingItemIndex: number | null = null;
  sequenceNumber: number = 0;
  MAX_CHAR_LIMIT: number = 50;
  isComponentArrayType: boolean | undefined = false;
  openClientFileManager = false;
  baseUrl: string = '';
  _pendingSequenceMap: Map<string, number> = new Map();
  isComponentRehydrated = false;
  showClientFileManagerCheckbox: boolean | undefined = false;
  isClientFileManagerDisabled: boolean | undefined = false;
  private accessionSub?: Subscription;

  propagateChange = (_: any) => { };
  onTouched = () => { };
  @Input('value') _value: any;

  @Input() datasource: any;
  availableDocuments: any[] = [];
  pagination = { page_size: 10, page_no: 0, count: 0 };
  uniqueString: string;
  cardData: any;
  manually_added: any[] = [];
  @Output() valueChange = new EventEmitter<any>();
  @Input() question: any;


  constructor(
    private _lazy: LazyService,
    private cd: ChangeDetectorRef,
    private ngZone: NgZone,
    private imageCompress: NgxImageCompressService,
    private baseService: BaseService,
    private storageService: StorageService,
    private alerts: AlertsService,
    private loader: LoaderService,
    private dialogService: DialogService,
    private communicationService: CommunicationService
  ) { }

  /**
   * Accessor for the component's value. Part of ControlValueAccessor.
   */
  get value(): any {
    return this._value;
  }

  /**
   * Setter for the component's value. Part of ControlValueAccessor.
   * Updates the internal value and notifies parent forms of the change.
   * @param val - The new value to set.
   */
  set value(val: any) {
    this._value = val;
    this.propagateChange(val);
    this.onTouched();
  }

  writeValue(value: any): void {
    this._value = value;
    if (value) {
      this.rehydrateSelectedImages(value);
    } else {
      this.selectedImages = [];
    }
  }


  /**
   * Registers a callback function that is called when the control's value changes in the UI.
   * Part of ControlValueAccessor.
   * @param fn - The callback function to register.
   */
  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  /**
   * Registers a callback function that is called by the forms API on "touch" events.
   * Part of ControlValueAccessor.
   * @param fn - The callback function to register.
   */
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  private rehydrateSelectedImages(value: any): void {
    let images = [];
    try {
      // Ensure the value is an array, parsing if it's a JSON string
      if (typeof value === 'string' && value.trim().startsWith('[')) {
        images = JSON.parse(value);
      } else if (Array.isArray(value)) {
        images = value;
      }

      if (!Array.isArray(images) || images.length === 0) {
        this.selectedImages = [];
        return;
      }

      // Process each image to construct the full imageUrl, similar to document-with-thumbnail
      this.selectedImages = images.map(image => {
        if (image.annotatedDocument && !image.imageUrl) {
          image.imageUrl = this.baseService.getApiEndpoint("dms") + 'emrImage/image-response-entity?args=id:' + image.annotatedDocument + '&application=dms';
        }
        return image;
      }).sort((a, b) => a.sequence - b.sequence); // Sort by sequence

      this.isComponentRehydrated = true;

    } catch (error) {
      console.error("Failed to parse or rehydrate selected images:", error);
      this.selectedImages = [];
    }
  }


  /**
   * Angular lifecycle hook that runs on component initialization.
   * It lazy-loads the required TUI Image Editor assets (CSS and JS)
   * and then initializes the editor instance.
   */
  ngOnInit(): void {
    Promise.all([
      this._lazy.loadDynamicStyle('./assets/css/tui-image-editor/tui-image-editor.css'),
      this._lazy.loadDynamicStyle('./assets/css/tui-image-editor/tui-color-picker.css'),
      this._lazy.loadScript('./assets/js/tui-image-editor/tui-color-picker.js'),
      this._lazy.loadScript('./assets/js/tui-image-editor/tui-image-editor.js')
    ])
      .then(() => {
        this.initEditor();
      })
      .catch(err => console.log('Failed to load TUI editor:', err));

    this.accessionSub = this.communicationService.emitAccessionIdentifierReady$.subscribe((accessionIdentifier) => {
      // Subscribe to dynamic updates of the accession identifier to automatically toggle the client file manager
      if (this.showClientFileManagerCheckbox) {
        if (accessionIdentifier) {
          this.openClientFileManager = true;
          this.isClientFileManagerDisabled = false;
        } else {
          this.openClientFileManager = false;
          this.isClientFileManagerDisabled = true;
        }
        this.cd.markForCheck();
      }
    });
  }

  /**
   * Fetches a paginated list of existing documents from a remote source
   * as defined by the `datasource` input property. It populates the
   * `availableDocuments` array for display.
   */
  loadAvailableDocument() {
    this.uniqueString = 'input-' + Math.random().toString(36).substring(2, 15);
    this.storageService.getSessionStorageData("currentData", data => {
      if (!data) return console.log('No currentData found in session storage');
      this.cardData = data;
      if (!this.datasource) return console.log('No datasource available');
      const apiResource = this.datasource;
      if (this.cardData && this.cardData.speciality_id == undefined && this.cardData.extra_data && this.cardData.extra_data.performingDoctorSpecialityId != undefined) {
        this.cardData['speciality_id'] = this.cardData.extra_data.performingDoctorSpecialityId;
      }
      apiResource.args = this.baseService.buildDyanamicStr(apiResource.args, { data: this.cardData });
      apiResource.filter = this.baseService.buildDyanamicStr(apiResource.filter, { data: this.cardData });
      const obj = {
        args: apiResource.args || '',
        filter: apiResource.filter || '',
        page_no: this.pagination.page_no,
        page_size: this.pagination.page_size
      };
      this.baseService
        .getData(obj, '', apiResource.resource, apiResource.application, apiResource.query_id)
        .subscribe(res => {
          if (res && res.errCode === 0) {
            this.pagination.count = res.count;
            this.availableDocuments = res.resource;
            this.getUrls();
          } else {
            this.alerts.error(res.message);
          }
        });
    });
  }

  /**
   * Handles the click event for the "Select File" button. If the `openClientFileManager`
   * flag is true, it opens a custom file manager dialog. After files are selected,
   * it converts the returned URLs into File objects and passes them to the `onChange` handler.
   * @param event - The mouse click event.
   */
  onSelectFile(event: MouseEvent): void {
    if (this.openClientFileManager) {
      event.preventDefault();
      const config = {
        title: 'Select Files',
        size: DialogModalSize.Lg,
        className: 'modal-lg',
        data: { baseUrl: this?.baseUrl, existingCount: this.selectedImages.length, isMultiSelect: this.isComponentArrayType }
      };
      this.dialogService.open(FileManagerComponent, config)
        .afterClosed
        .subscribe(async (result: Array<{imageUrl:string,seq:number}>) => {
          if (result && Array.isArray(result) && result.length > 0) {
            // Convert URLs to File objects and then pass to onChange
            // MODIFICATION: Store the returned sequences in the pending map before processing
            this._pendingSequenceMap.clear();
            for (const {imageUrl, seq} of result) {
              const filename = imageUrl.substring(imageUrl.lastIndexOf('/') + 1);
              this._pendingSequenceMap.set(filename, seq);
            }

            this.loader.showSpinner(); // Show spinner during file conversion
            const files: File[] = [];
            for (const {imageUrl, seq} of result) {
              try {
                const response = await fetch(imageUrl);
                const blob = await response.blob();
                const filename = imageUrl.substring(imageUrl.lastIndexOf('/') + 1);
                const file = new File([blob], filename, { type: blob.type });
                files.push(file);
              } catch (error) {
                console.error(`Error fetching or converting image from URL: ${imageUrl.substring(imageUrl.lastIndexOf('/') + 1)}`, error);
                this.alerts.error(`Failed to process image from URL: ${imageUrl.substring(imageUrl.lastIndexOf('/') + 1)}`);
              }
            }
            this.loader.hideSpinner(); // Hide spinner after conversion

            // Simulate a native file input event
            const simulatedEvent = {
              target: {
                files: files
              }
            } as any; // Cast to any to match the onChange event signature loosely

            // Call the existing onChange method to process these files
            this.onChange(simulatedEvent);
          }
        }, err => {
          console.error('Error opening File Manager:', err);
          this.alerts.error('Failed to open file manager.');
        });
    }
  }

  /**
   * Deletes a document from the available documents.
   *
   * This method deletes a document from the available documents, updates the
   * sequences of the remaining documents, and emits the updated value to the
   * parent component.
   *
   * @param document - The document to be deleted.
   * @param index - The index of the document in the available documents array.
   */
  deleteDoc(document , index){
    this.loader.showSpinner();
    if (document.image) {
      URL.revokeObjectURL(document.image);
    }
    this.selectedImages.splice(index, 1);
    this.updateSequences();
    this.value = JSON.stringify(this.selectedImages);
    this.valueChange.emit(this.value);
    this.loader.hideSpinner();
  }

  
  /**
   * Iterates through the `availableDocuments` array and constructs the full,
   * authenticated image URL for each document using the `baseService`.
   */
  getUrls() {
    if (this.availableDocuments != undefined && this.availableDocuments.length != undefined && this.availableDocuments.length) {
      this.availableDocuments.forEach(document => {
        document.imageUrl = this.baseService.getApiEndpoint("dms") + (document.dms_resource ? document.dms_resource : document.document_type ? document.document_type : 'emrImage') + '/image-response-entity?args=id:' + (document.imageId ? document.imageId : document.documentId) + '&application=dms';
      });
    }
  }

  /**
   * Navigates to the previous page of available documents and re-fetches the list.
   */
  goPrev() {
    if (this.pagination.page_no > 0) {
      this.pagination.page_no--;
      this.ngOnInit();
    }
  }

  /**
   * Navigates to the next page of available documents and re-fetches the list.
   */
  goNext() {
    this.pagination.page_no++;
    this.ngOnInit();
  }

  /**
   * Determines the CSS class for the "Next" pagination button to disable it
   * when the user is on the last page.
   * @returns A string, either 'errors' to disable the button or '' to enable it.
   */
  getClassToEnableDisableIcon() {
    const { count, page_size, page_no } = this.pagination;
    // If the total number of items is less than or equal to the page size,
    // there's only one page, so the 'Next' button should be disabled.
    if (count <= page_size) {
      return 'errors';
    }
    // Otherwise, calculate the last page and check if we are on it.
    const lastPage = Math.ceil(count / page_size) - 1;
    return page_no < lastPage ? '' : 'errors';
  }

  /**
   * Initializes the TUI Image Editor instance after its scripts have been loaded.
   * It patches the editor's prototype to intercept the "Save" or "Download"
   * action, redirecting it to the custom `onEditorSave` method.
   */
  initEditor() {
    const container = document.getElementById('image-editor-container');
    if (!container || !tui?.ImageEditor) {
      console.error('TUI ImageEditor not available');
      return;
    }

    const width = container!.clientWidth;
    const height = container!.clientHeight;

    // ─── Patch TUI so that its internal `download()` calls your handler ───
    const proto: any = tui.ImageEditor.prototype;
    if (!proto.__patchedDownload) {
      const originalGetActions = proto.getActions;
      proto.getActions = function (this: any) {
        const actions = originalGetActions.call(this);
        // keep a reference to the original download:
        const origDownload = actions.main.download;
        // override:
        actions.main.download = () => {
          // if you’ve attached a custom handler, call it:
          if (this._customSaveHandler) {
            this._customSaveHandler();
            console.log('Custom save handler called');
          } else {
            // fallback: run the default behavior
            origDownload.call(this);
            console.log('Default download action called');
          }
        };
        return actions;
      };
      proto.__patchedDownload = true;  // ensure you only patch once
    }

    this.editor = new tui.ImageEditor(container, {
      includeUI: {
        loadImage: {
          path: '',
          name: 'Blank'
        },
        menu: ['crop', 'flip', 'rotate', 'draw', 'shape', 'text'],
        uiSize: {
          width: '100%',
          height: '700px'
        },
        menuBarPosition: 'bottom'
      },
      cssMaxWidth: width,
      cssMaxHeight: height - 64,
      selectionStyle: {
        cornerSize: 20,
        rotatingPointOffset: 70
      }
    });

    (this.editor as any)._customSaveHandler = () => this.onEditorSave();

    this.loadAvailableDocument();
    this.fetchClientSystemBaseURL();
    if (this?.question?.type.toLowerCase() === "array") {
      this.isComponentArrayType = true;
    }

    // After rehydrating, load the first image into the editor for a good UX
    if (this.editor && this.selectedImages.length > 0) {
      this.loadImageToEditor({
        documentName: this.selectedImages[0].documentName,
        imageUrl: this.selectedImages[0].imageUrl
      });
    }
      
    // Ensure Angular detects the changes
    this.cd.markForCheck();
  }

  /**
   * Fetches the base URL for the client-side file manager from system configuration
   * and sets the state of the "Enable Client File Manager" checkbox accordingly.
   *
   * This function performs the following logic:
   * 1. It calls an API to get the `file_system_ip_address` configuration.
   * 2. It checks sessionStorage for a `procedureAccessionIdentifier`.
   * 3. Based on these two conditions, it controls the checkbox's state:
   *    - If the config is found and an accession ID exists, the checkbox is shown, checked, and enabled.
   *    - If the config is found but no accession ID exists, the checkbox is shown, unchecked, and disabled.
   *    - If the config is not found, the checkbox is hidden entirely.
   */
  fetchClientSystemBaseURL() {
    const obj = {
      filter: `code:file_system_ip_address,module_code:emr`,
      sort: 'g_modify_time desc'
    };
    this.baseService.getData(obj, '', 'config_master', 'system-management', 'GET_PAGE').subscribe((data: any) => {

      const configExists = data?.errCode === 0 && data?.resource?.length > 0;
      const accessionIdentifier = sessionStorage.getItem('procedureAccessionIdentifier');

      this.showClientFileManagerCheckbox = configExists;

      if (configExists) {
        this.baseUrl = data?.resource[0]?.default_value;
        if(accessionIdentifier) {
          this.openClientFileManager = true;
          this.isClientFileManagerDisabled = false;
        } else {
          this.openClientFileManager = false;
          this.isClientFileManagerDisabled = true;
        }
      } else {
        this.openClientFileManager = false;
        this.isClientFileManagerDisabled = true;
        console.log('No base URL found for client system.');
      }
    });
  }


  /**
   * Corrects a filename to ensure its extension matches the actual MIME type of its blob.
   * It handles common aliases (e.g., 'jpg' for 'image/jpeg') and strips incorrect
   * extensions before appending the correct one.
   * @param rawName - The original or intended filename.
   * @param blob - The file's Blob object, used to determine the true MIME type.
   * @returns A normalized filename with the correct extension.
   */
  normalizeFileName(rawName: string, blob: Blob): string {
    const mimeType = blob.type;
    const currentExt = rawName.split('.').pop()?.toLowerCase();

    // Define common aliases for image types
    const extensionAliases = {
      'image/jpeg': ['jpg', 'jpeg', 'jfif', 'pjpeg', 'pjp'],
      'image/png': ['png'],
      'image/gif': ['gif'],
      'image/webp': ['webp']
    };

    // Get the valid extensions for the blob's MIME type
    const validExtensions = extensionAliases[mimeType];

    // If we have a list of valid extensions and the current extension is in that list, the name is fine.
    if (validExtensions && currentExt && validExtensions.includes(currentExt)) {
      return rawName;
    }

    // Otherwise, build a new name with the primary extension.
    const primaryExt = (validExtensions && validExtensions[0]) || mimeType.split('/')[1] || 'png';
    const baseName = rawName.includes('.') ? rawName.substring(0, rawName.lastIndexOf('.')) : rawName;
    
    return `${baseName}.${primaryExt}`;
  }

  /**
   * Converts a base64 data URL string into a Blob object.
   * @param dataURL - The base64 data URL (e.g., "data:image/png;base64,...").
   * @returns A Blob object representing the image data.
   */
  dataURLtoBlob(dataURL: string): Blob {
    // Split into "data:[<mediatype>][;base64],<data>"
    const parts = dataURL.split(',');
    const meta  = parts[0];               // e.g. "data:image/png;base64"
    const b64   = parts[1];
    
    // Decode base64 string
    const byteString = atob(b64);
    const len        = byteString.length;
    const arr        = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      arr[i] = byteString.charCodeAt(i);
    }
    
    // Extract the MIME type from the meta part
    const mimeMatch = meta.match(/data:([^;]+);base64/);
    const mimeType  = mimeMatch ? mimeMatch[1] : 'application/octet-stream';

    return new Blob([arr], { type: mimeType });
  }

  /**
   * Finds the index of an image within the `selectedImages` array based on its filename.
   * @param fileName - The name of the image to find.
   * @returns The index of the image, or -1 if not found.
   */
  findExistingImageIndex(fileName: string): number {
    // Compare without extensions to avoid mismatches.
    return this.selectedImages.findIndex(img => img.documentName.replace(/\.[^/.]+$/, '') === fileName.replace(/\.[^/.]+$/, ''));
  }

  /**
   * Exports the currently edited image from the TUI editor, normalizes its filename,
   * and passes it to the `processImage` function for uploading and state update.
   */
  async saveImage(): Promise<void> {
    // 1) grab the edited image
    const dataUrl = this.editor.toDataURL({ format: 'jpeg', quality: 0.8 });

    const blob    = this.dataURLtoBlob(dataUrl);
    const rawName = this.editor.getImageName();       // e.g. "image 1"

    // b) Normalize fileName
    const fileName = this.normalizeFileName(rawName, blob);
    const fileBlob = new File([blob], fileName, { type: blob.type });

    const idx = this.findExistingImageIndex(fileName);
    const sequence = idx > -1 ? this.selectedImages[idx].sequence : undefined;

    await this.processImage(fileBlob, fileName, dataUrl, sequence);

    // After adding, re-sort the array to maintain order.
    this.selectedImages.sort((a, b) => a.sequence - b.sequence);
    this.value = JSON.stringify(this.selectedImages);
    this.valueChange.emit(this.value);

    this.alerts.success('Request Successful.');
  }

  /**
   * Custom handler for the TUI editor's save action. It triggers the image save
   * process and then prompts the user to apply the same crop to all other
   * selected images if applicable.
   */
  async onEditorSave(): Promise<void> {
    this.loader.showSpinner();
    try{
      await this.saveImage();

      const singleMode = !this.isComponentArrayType;
      const alreadyPrompted = this.firstBatchPromptDone;
      const rawFlag = sessionStorage.getItem('isCropped');
      const isCropped = Boolean(rawFlag && JSON.parse(rawFlag));
      const isMulti = this.selectedImages.length > 1;

      if (singleMode || alreadyPrompted || !isCropped || !isMulti) {

        if (singleMode) console.log('Single image mode is on. Skipping multi‑crop prompt.');
        else if (alreadyPrompted) console.log('Multi-crop confirmation already shown. Skipping…');
        else if (!isCropped) console.log('Crop not applied. Skipping multi‑crop prompt.');
        else console.log('Less than 2 images available. Skipping multi‑crop prompt.');

        this.firstBatchPromptDone = true;
        this.selectedImages = [...this.selectedImages];
        return;
      }

      // Show prompt only for the first multi-image crop
      this.firstBatchPromptDone = true;

      this.baseService.modalService.confirmCustomBtn( "Confirmation", "Do you want to apply the same crop on all images?", "sm").subscribe(async result => {
        try {
          if (result) {
            const stored = sessionStorage.getItem('lastCropRect');
            if (!stored) {
              console.log('No stored cropRect found');
              return;
            }
            const cropRect = JSON.parse(stored);
            const activeName = this.editor._graphics.imageName;
            const originalSize = { width: this.originalImageSizes.newWidth, height: this.originalImageSizes.newHeight };
  
            const cropPromises = this.selectedImages
              .filter(img => img.documentName.replace(/\.[^/.]+$/, '') !== activeName.replace(/\.[^/.]+$/, ''))
              .map(img => this.applyCropToImage(img.documentName, cropRect, originalSize));
  
            await Promise.all(cropPromises);
            this.alerts.success('Request Successful.');
  
          } else {
            console.log("User choose not to apply crop to all images.");
            return;
          }
        } catch (error) {
          console.error('An error occurred during the multi-crop operation:', error);
          this.alerts.error('An error occurred during multi-crop. Not all images may have been updated.');
        } finally {
          this.finalizeImageUpdates();
          this.loader.hideSpinner();
        }
      });

    } catch (error) {
      console.error('An error occurred during the save/crop operation:', error);
      this.alerts.error('An error occurred. Not all images may have been updated.');
      this.loader.hideSpinner();
    }
  }

  finalizeImageUpdates(): void {
    // Once all images in the batch are uploaded, sort the entire array by sequence
    this.selectedImages.sort((a, b) => a.sequence - b.sequence);

    // Update the form value and emit the change
    this.value = JSON.stringify(this.selectedImages);
    this.valueChange.emit(this.value);

    // Update sequenceNumber to reflect the new total count
    this.sequenceNumber = this.selectedImages.length;
    this.cd.markForCheck();
  }

  /**
   * Applies a given crop transformation to a specified image. It fetches the image,
   * draws the cropped portion onto a new canvas, and then calls `processImage`
   * to upload the newly cropped version.
   * @param imageName - The name of the image to apply the crop to.
   * @param cropRect - The dimensions and position of the crop area.
   * @param originalSize - The original dimensions of the image within the editor.
   */
  async applyCropToImage(
    imageName: string,
    cropRect: { left: number, top: number, width: number, height: number },
    originalSize: { width: number, height: number }
  ): Promise<void> {
    // Find the target image URL
    const idx = this.findExistingImageIndex(imageName);
    if (idx < 0) {
      console.log(`Image "${imageName}" not found for cropping.`);
      return;
    }

    const imgData = this.selectedImages[idx];
    const sequence = imgData.sequence; // Preserve the original sequence

    try {
      // Step 1: Fetch the original image data
      const { documentName, base64String } = await this.fetchImageFromUrl({
        imageUrl: imgData.imageUrl,
        name: imgData.documentName
      });

      // Step 2: Perform the crop operation on a canvas (promisified)
      const newUrl = await new Promise<string>((resolve, reject) => {
        const imgEl = document.createElement('img');
        imgEl.onload = () => {
          const scaleX = imgEl.naturalWidth / originalSize.width;
          const scaleY = imgEl.naturalHeight / originalSize.height;

          const canvas = document.createElement('canvas');
          canvas.width = cropRect.width * scaleX;
          canvas.height = cropRect.height * scaleY;
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            return reject(new Error('Failed to get 2D context from canvas.'));
          }

          ctx.drawImage(
            imgEl,
            cropRect.left * scaleX, cropRect.top * scaleY,
            cropRect.width * scaleX, cropRect.height * scaleY,
            0, 0,
            canvas.width, canvas.height
          );
          resolve(canvas.toDataURL('image/jpeg', 0.8));
        };
        imgEl.onerror = (err) => reject(new Error(`Failed to load image for cropping: ${err}`));
        imgEl.src = base64String;
      });

      // Step 3: Create a new File object from the cropped image data
      const croppedBlob = this.dataURLtoBlob(newUrl);
      const croppedFile = new File([croppedBlob], documentName, { type: croppedBlob.type });

      // Step 4: Process and upload the new image, replacing the old one
      await this.processImage(croppedFile, documentName, newUrl, sequence);

    } catch (error) {
      this.alerts.error(`Failed to apply crop to "${imageName}".`);
      console.error(`Error in applyCropToImage for "${imageName}":`, error);
      throw error; // Re-throw to allow Promise.all to catch and report the failure
    }
  }

  /**
   * Fetches an image from a remote URL, converts it to a File object,
   * and then calls `processImage` to add it to the selection.
   * @param image - An object containing the `imageUrl` and `name` of the hosted image.
   */
  loadHostedImage(image): void {
    console.log("hosted image:: ", image);
    this.loader.showSpinner();
    this.baseService.getFileByUrl(image.imageUrl, false).toPromise()
    .then(async blob => {
      if (blob) {
        const normalizedFileName = this.normalizeFileName(image.name, blob);
        if (this.findExistingImageIndex(normalizedFileName) !== -1) {
          console.log(`Image "${normalizedFileName}" is already selected.`);
          return;
        }
        const originalFile = new File([blob], normalizedFileName, { type: blob.type });
        const base64String = await this.blobToDataURL(blob);
        const sequence = this.selectedImages.length + 1;

        await this.processImage(originalFile, normalizedFileName, base64String, sequence);

        if (this.selectedImages.length > 0) {
            this.loadImageToEditor({
              documentName: this.selectedImages[0].documentName, 
              imageUrl: this.selectedImages[0].imageUrl
            });
        }

        this.selectedImages.sort((a, b) => a.sequence - b.sequence);
        this.value = JSON.stringify(this.selectedImages);
        this.valueChange.emit(this.value);
      }
    })
    .catch ((err) => {
      console.error('Failed to load hosted image:', err);
      this.alerts.error('Could not load image. Please try again.');
    })
    .finally(() => {
      this.loader.hideSpinner();
    });
  }

  /**
   * Converts a Blob object into a base64 data URL string.
   * @param blob - The Blob to convert.
   * @returns A promise that resolves with the base64 data URL.
   */
  blobToDataURL(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload    = () => resolve(reader.result as string);
      reader.onerror   = e => reject(e);
      reader.readAsDataURL(blob);
    });
  }

  /**
   * Handles the file selection event from a native file input. It processes
   * each selected file, sanitizes its name, compresses it if necessary,
   * and then calls `processImage` to upload and add it to the selection.
   * @param event - The file input change event.
   */
  onChange(event: any): void {
    const files: FileList = event.target.files;
    if (!files || files.length === 0) {
      return;
    }

    let filesToProcess = [];
    if(!this.isComponentArrayType) {
      filesToProcess = [files[0]];
      this.selectedImages = [];
    } else {
      filesToProcess = Array.from(files);
    }

    this.loader.showSpinner();
    const usePendingSequences = this._pendingSequenceMap && this._pendingSequenceMap.size > 0;

    const processingPromises = Array.from(filesToProcess).map((file, index) => {
        return new Promise<void>((resolve, reject) => {
          let originalFile = file;
          const sanitizedName = this.sanitizeFileName(originalFile.name);

          // If sanitized, create a new File object
          if (sanitizedName !== originalFile.name) {
            console.log(`Original Name: "${originalFile.name}" -> Sanitized Name: "${sanitizedName}"`);
            originalFile = new File([originalFile], sanitizedName, { type: originalFile.type });
          }

          // Check for duplicates before processing the file
          const existingIndex = this.findExistingImageIndex(sanitizedName);
          if (existingIndex !== -1) {
            console.log(`Skipping duplicate image: "${sanitizedName}"`);
            return reject();
          }

          let sequence: number;
          if (usePendingSequences && this._pendingSequenceMap.has(originalFile.name)) {
            sequence = this._pendingSequenceMap.get(originalFile.name)!;
          } else {
            sequence = (this.selectedImages.length) + index + 1;
          }

          this.getOriginalFileSize(originalFile).then(async (originalSizeBytes) => {
            const base64Image = await new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = (e) => resolve(e.target?.result as string);
              reader.onerror = (err) => reject(err);
              reader.readAsDataURL(originalFile);
            });
            let imageUrl = await this.compressImage(base64Image, originalSizeBytes);

            const compressedBlob = this.dataURLtoBlob(imageUrl);
            const compressedFile = new File([compressedBlob], sanitizedName, { type: compressedBlob.type });

            this.processImage(compressedFile, sanitizedName, imageUrl, sequence).then(resolve).catch(reject);
          }).catch(err => {
            console.log(`Could not process file ${originalFile.name}:`, err);
            reject(err);
          })
        });
    });

    Promise.all(processingPromises)
      .then(() => {
        // Once all images in the batch are uploaded, sort the entire array by sequence
        this.selectedImages.sort((a, b) => a.sequence - b.sequence);

        // Update the form value and emit the change
        this.value = JSON.stringify(this.selectedImages);
        this.valueChange.emit(this.value);

        // Update sequenceNumber to reflect the new total count
        this.sequenceNumber = this.selectedImages.length;
        
        this.alerts.success('Request Successful.');

        if (this.selectedImages.length > 0) {
            this.loadImageToEditor({
              documentName: this.selectedImages[0].documentName, 
              imageUrl: this.selectedImages[0].imageUrl
            });
        }
      })
      .catch(error => {
        console.error('An error occurred during batch image processing:', error);
        this.alerts.error('Some images failed to upload.');
      })
      .finally(() => {
        this.loader.hideSpinner();
        // Clear the pending map and file input after the operation is complete
        this._pendingSequenceMap.clear();
        if (event.target) {
          event.target.value = '';
        }
      });
  }

  /**
   * The core function for processing an image. It uploads the provided file blob
   * to the server. On success, it updates the `selectedImages` array, either by
   * replacing an existing item or appending a new one, and then updates the component's value.
   * @param fileBlob - The image data as a File or Blob.
   * @param fileName - The name of the file.
   * @param base64 - The base64 representation of the image, used for upload.
   */
  processImage(fileBlob, fileName, base64, sequence?: number): Promise<void> {

    return new Promise((resolve, reject) => {

      const idx = this.findExistingImageIndex(fileName);

      if (idx === -1) {
        console.log(`New image find "${fileName}".`);
      }
      const existing = this.selectedImages[idx];

      const fd = new FormData();
      fd.append('action', 'add');
      fd.append('fileName', fileName);
      fd.append('file', fileBlob);

      this.loader.showSpinner();

      this.baseService.upload(fd, 'dms', 'emrImage', base64).subscribe(res => {
        this.loader.hideSpinner();
        if (res.errCode !== 0) {
          this.alerts.error(res.message);
          return reject();
        }

        const newObj = {
          document: existing?.imageId ? existing?.imageId : existing?.documentId,
          annotatedDocument: res.documentId,
          documentName: fileName ? fileName : res.documentName ? res.documentName : "",
          imageUrl: this.baseService.getApiEndpoint("dms") + "emrImage/image-response-entity?args=id:" + res.documentId + "&application=dms",
          sequence: sequence
        };

        if (idx > -1) {
          this.selectedImages[idx] = newObj;
        } else {
          this.selectedImages.push(newObj);
        }

        resolve();
        },
        err => {
          this.loader.hideSpinner();
          this.alerts.error('Upload failed', err);
          reject(err);
        });
    });
  }

  /**
   * Sanitizes a filename to meet specific constraints. It replaces multiple dots,
   * trims the name to a max character limit, and appends a short hash to ensure uniqueness
   * while maintaining readability.
   * @param fileName - The original filename.
   * @returns A sanitized filename.
   */
  sanitizeFileName(fileName: string): string {
    const lastDotIndex = fileName.lastIndexOf('.');
    if (lastDotIndex < 0) return fileName;

    const baseName = fileName.substring(0, lastDotIndex);
    const extension = fileName.substring(lastDotIndex);  // includes the “.”
    const totalDots = (fileName.match(/\./g) || []).length;

    // Only sanitize if there’s more than one dot or it’s too long
    if (totalDots <= 1 && fileName.length <= this.MAX_CHAR_LIMIT) {
      return fileName;
    }

    // 1) Replace all internal dots with hashes
    const baseWithHashes = baseName.replace(/\./g, '#');

    // 2) Create a 6‑char deterministic hash from the full filename
    const hashPart = `#${this.simpleHash(fileName)}`;   // length = 7

    // 3) Compute how many chars remain for the base
    const extensionLen = extension.length;
    const hashPartLen = hashPart.length;
    const remainingLen = this.MAX_CHAR_LIMIT - extensionLen - hashPartLen;

    // 4) Trim the base symmetrically to fit
    let trimmedBase: string;
    if (baseWithHashes.length <= remainingLen) {
      // If shorter, you could pad here—but often not needed
      trimmedBase = baseWithHashes;
    } else {
      const firstHalfLen = Math.floor(remainingLen / 2);
      const secondHalfLen = remainingLen - firstHalfLen;
      const startSegment = baseWithHashes.substring(0, firstHalfLen);
      const endSegment = baseWithHashes.substring(baseWithHashes.length - secondHalfLen);
      trimmedBase = startSegment + endSegment;
    }

    // 5) Concatenate everything
    return trimmedBase + hashPart + extension;
  }

  /**
   * Generates a simple, deterministic 6-character hash from a string.
   * @param input - The string to hash.
   * @returns A 6-character hash string.
   */
  simpleHash(input: string): string {
    let hash = 0;
    for (let i = 0; i < input.length; i++) {
      hash = ((hash << 5) - hash) + input.charCodeAt(i);
      hash |= 0;  // to 32‑bit int
    }
    // Convert to positive base‑36 and take 6 chars
    return (Math.abs(hash)).toString(36).substring(0, 6);
  }

  /**
   * Asynchronously gets the size of a File object in bytes.
   * @param file - The File object to measure.
   * @returns A promise that resolves with the file size in bytes.
   */
  async getOriginalFileSize(file: File): Promise<number> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target && event.target.result instanceof ArrayBuffer) {
          resolve(event.target.result.byteLength);
        } else {
          reject(new Error('Failed to read file as ArrayBuffer.'));
        }
      };
      reader.onerror = (error) => {
        console.error('FileReader error:', error);
        reject(error);
      };
      reader.readAsArrayBuffer(file);
    });
  }

  /** Check if compression is needed (e.g., >1MB) */
  async isCompressionNeeded(size: number): Promise<boolean> {
    return size >= 1048576;
  }

  /**
   * Compresses a base64 encoded image if its size exceeds a threshold.
   * @param base64Image - The base64 string of the image to compress.
   * @param size - The original size of the image in bytes.
   * @returns A promise that resolves with the (potentially compressed) base64 image string.
   */
  async compressImage(base64Image, size): Promise<string> {
    if (!(await this.isCompressionNeeded(size))) {
      console.log("No compression needed.");
      return Promise.resolve(base64Image);
    }

    const orientation = -1; // DOC_ORIENTATION.Default
    const ratio = 50;       // Reduce dimensions to 50%
    const quality = 50;     // JPEG quality 50%

    const compressedImage = await this.imageCompress.compressFile(
      base64Image,
      orientation,
      ratio,
      quality
    );
    const tempFile = this.dataURLtoFile(compressedImage, 'tempImageForSizeCheck.' + size);
    const compressedSize = await this.getOriginalFileSize(tempFile);
    console.log('Compressed image size (bytes):', compressedSize);

    return Promise.resolve(compressedImage);
  }

  /**
   * Converts a base64 data URL into a File object.
   * @param dataurl - The base64 data URL.
   * @param filename - The desired name for the output file.
   * @returns A new File object.
   */
  dataURLtoFile(dataurl, filename) {
    var arr = dataurl.split(','), mime = arr[0].match(/:(.*?);/)[1],
      bstr = atob(arr[1]), n = bstr.length, u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  }

  /**
   * Fetches an image from a URL, handling authentication via `baseService`.
   * It normalizes the filename and returns the image data as a File and base64 string.
   * @param image - An object containing the `imageUrl` and `name`.
   * @returns A promise that resolves with the file, its normalized name, and its base64 data.
   */
  fetchImageFromUrl(image: { imageUrl: string; name: string; }): Promise<{ file: File, documentName: string, base64String: string }> {
    return this.baseService.getFileByUrl(image.imageUrl, false).toPromise()
    .then(async blob => {
      if (blob) {
        const originalFile = new File([blob], image.name, { type: blob.type });
        const base64String = await this.blobToDataURL(blob);
        const normalizedName = this.normalizeFileName(image.name, blob);
        return {
          file: originalFile,
          documentName: normalizedName,
          base64String: base64String
        };
      }
    })
    .catch ((err) => {
      console.error('Failed to load hosted image:', err);
      this.alerts.error('Could not load image. Please try again.');
      return err;
    });
  }


  /**
   * Loads an image into the TUI editor instance for editing. It fetches the image
   * data first to handle authentication and CORS, then loads it into the editor.
   * @param image - An object containing the `documentName` and `imageUrl`.
   */
  loadImageToEditor(image: { documentName: string; imageUrl; }): void {
    if (!this.editor) return;

    const name = image.documentName || 'loadedImage';
    this.loader.showSpinner();
    this.fetchImageFromUrl({imageUrl: image.imageUrl, name: image.documentName}).then(({ file, documentName, base64String }) => {
      this.editor.loadImageFromURL(base64String, documentName).then((sizeValue) => {
        this.originalImageSizes = sizeValue;
        this.editor.clearUndoStack();
        this.editor.ui.activeMenuEvent();
      })
      .catch(err => {
        console.error('TUI ImageEditor load failed:', err);
      })
      .finally(() => {
        this.loader.hideSpinner();
      });
    })
    .catch(err => {
      console.error('Error fetching image for editor:', err);
    })
    .finally(() => {
      this.loader.hideSpinner();
    })

  }

  // --- Drag and Drop Methods (Start) ---

  /**
   * Handles the drag start event for reordering items.
   *
   * This method is called when a drag operation starts on an item. It sets the
   * draggingItemIndex to the index of the dragged item and adds a 'dragging'
   * class to the dragged element for visual feedback.
   *
   * @param event - The drag event.
   * @param index - The index of the dragged item.
   */
  onDragStart(event: DragEvent, index: number): void {
    this.draggingItemIndex = index;
    if (event.target instanceof HTMLElement) {
      event.target.classList.add('dragging');
    }
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', index.toString()); // Necessary for Firefox
    }
  }

  /**
   * Handles the drag over event for reordering items.
   *
   * This method is called when an item is dragged over another item. It
   * prevents the default behavior to allow the drop and adds a 'drag-over'
   * class to the target element for visual feedback.
   *
   * @param event - The drag event.
   * @param index - The index of the target item.
   */
  onDragOverItem(event: DragEvent, index: number): void {
    event.preventDefault();
    const target = event.currentTarget as HTMLElement;

    // Add visual feedback class
    if (!target.classList.contains('drag-over')) {
      document.querySelectorAll('.imageContainerselct.drag-over').forEach(el => {
        el.classList.remove('drag-over');
      });
      target.classList.add('drag-over');
    }
  }

  /**
   * Handles the drop event for reordering items.
   *
   * This method is called when an item is dropped on another item. It
   * reorders the items in the tempArray and updates the sequences of the
   * items.
   *
   * @param event - The drop event.
   * @param dropIndex - The index of the target item where the dragged item is dropped.
   */
  onDropOnItem(event: DragEvent, dropIndex: number): void {
    event.preventDefault();
    event.stopPropagation(); // Prevent the event from bubbling up to the container's drop handler

    const startIndex = this.draggingItemIndex;

    console.log("selectedImages is ", this.selectedImages);

    if (startIndex === null || startIndex === dropIndex) {
      this.cleanupDragState();
      return;
    }

    const itemToMove = this.selectedImages[startIndex];

    // Remove from original position
    this.selectedImages.splice(startIndex, 1);

    // Insert into new position in the modified array
    this.selectedImages.splice(dropIndex, 0, itemToMove);
    this.updateSequences();

    console.log('selectedImages AFTER drop ON ITEM:', this.selectedImages);

    this.value = JSON.stringify(this.selectedImages);
    this.valueChange.emit(this.value);
    this.cleanupDragState();
  }


  /**
   * Handles the drag over event for the container.
   *
   * This method is called when an item is dragged over the container. It
   * prevents the default behavior to allow the drop on the container itself
   * (e.g., empty spaces) and sets the drop effect to 'move'.
   *
   * @param event - The drag event.
   */
  onDragOverContainer(event: DragEvent): void {
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
  }

  /**
   * Handles the drop event on the container.
   *
   * This method is called when an item is dropped on the container. It
   * determines the insertion index based on the drop position within the
   * container, reorders the items in the tempArray, updates the sequences of
   * the items, and emits the updated value to the parent component.
   *
   * @param event - The drop event.
   */
  onDropInContainer(event: DragEvent): void {
    event.preventDefault();
    const startIndex = this.draggingItemIndex;

    if (startIndex === null) {
      this.cleanupDragState();
      return;
    }

    const itemToMove = this.selectedImages[startIndex];

    // Determine insertion index based on drop position within the container
    let targetDropIndex = this.selectedImages.length - 1; // Default to end if logic below doesn't find a spot
    if (event.currentTarget instanceof HTMLElement) {
      const listElement = event.currentTarget;
      const childElements = Array.from(listElement.children).filter(
        el => el.classList.contains('imageContainerselct') && !el.classList.contains('drag-over')
      ) as HTMLElement[];

      targetDropIndex = childElements.length; // Default to end of list (current items)

      for (let i = 0; i < childElements.length; i++) {
        const el = childElements[i];
        const rect = el.getBoundingClientRect();
        if (event.clientX < rect.left + rect.width / 2) {
          targetDropIndex = i;
          break;
        }
      }
    }

    // Remove from old position first
    this.selectedImages.splice(startIndex, 1);

    // Insert into new position in the modified array
    this.selectedImages.splice(targetDropIndex, 0, itemToMove);
    this.updateSequences();

    console.log('selectedImages AFTER drop ON CONTAINER:', this.selectedImages);
    this.value = JSON.stringify(this.selectedImages);
    this.valueChange.emit(this.value);
    this.cleanupDragState();
  }


  /**
   * Handles the drag end event.
   *
   * This method is called when a drag operation ends. It prevents the
   * default behavior and calls the cleanupDragState method to reset the drag
   * state.
   *
   * @param event - The drag event.
   */
  onDragEnd(event: DragEvent): void {
    event.preventDefault();
    this.cleanupDragState();
  }

  /**
   * Updates the sequence numbers of the items in the tempArray.
   *
   * This method iterates through the items in the tempArray and updates their
   * sequence numbers based on their current index in the array.
   */
  updateSequences() {
    this.selectedImages.forEach((item, index) => {
      item.sequence = index + 1;
    });
    this.sequenceNumber = this.selectedImages.length;
  }

  /**
   * Cleans up the drag state.
   *
   * This method resets the drag state by removing the 'drag-over' class from
   * any elements that have it and setting the draggingItemIndex to null.
   */
  cleanupDragState(): void {
    if (this.draggingItemIndex !== null) {
      // Attempt to find the element that was being dragged and remove class
      // This is safer if the component re-renders and element references change
      document.querySelectorAll('.imageContainerselct.drag-over').forEach(el => {
        el.classList.remove('drag-over');
      });
    }
    this.draggingItemIndex = null;
  }

  /**
   * Angular lifecycle hook that is called when the component is destroyed.
   *
   * This method calls the cleanupObjectUrls method to revoke object URLs and
   * prevent memory leaks.
   */
  ngOnDestroy(): void {
    sessionStorage.removeItem('lastCropRect');
    sessionStorage.removeItem('isCropped');
    if (this.accessionSub) {
      // Clean up subscription to prevent memory leaks when the editor is destroyed
      this.accessionSub.unsubscribe();
    }
  }

}
